import EditIcon from "@mui/icons-material/Edit";
import {
  AlertColor,
  Box,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import BoxContent from "../../../components/boxContent";
import DataGridApi from "../../../components/dataGridApi";
import QuickAlert from "../../../components/form/quickAlert";
import { IVwLoyalty } from "../../../services/loyaltyService";
import UserModal from "./userModalControl/userModalControl";
import ModalLoyaltyReport from "../modalLoyaltyReport/ModalLoyaltyReport";
import dayjs from "dayjs";

type ActiveFilter = "" | "true" | "false";

export default function LoyaltyEligibility() {
  const { t } = useTranslation();

  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [openModalReport, setOpenModalReport] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IVwLoyalty | null>(null);
  const [dataUpdated, setDataUpdated] = useState(0);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [messageQuickAlert, setMessageQuickAlert] = useState("");
  const [severityAlert, setSeverityAlert] = useState<AlertColor>("success");
  const [titleMessage, setTitleMessage] = useState("");

  const watchDataChange = (successMessage?: string) => {
    setDataUpdated((n) => n + 1);
    setVisibleAlert(true);
    setTitleMessage(t("quickAlert.sucess"));
    setMessageQuickAlert(successMessage ?? t("loyalty.edit.success"));
    setSeverityAlert("success");
  };

  const messageError = (errorMessage?: string) => {
    setVisibleAlert(true);
    setTitleMessage(t("quickAlert.error2"));
    setMessageQuickAlert(errorMessage ?? t("loyalty.edit.error"));
    setSeverityAlert("error");
  };

  const openReportSuccessAlert = (message?: string) => {
    setSeverityAlert("success");
    setTitleMessage(t("common.success"));
    setMessageQuickAlert(message ?? t("loyalty.report.successMessage"));
    setVisibleAlert(true);
  };

  const handleEditClick = (e: React.MouseEvent, row: any) => {
    e.stopPropagation();
    setSelectedRow(row);
    setOpenModalEdit(true);
  };

  const columns: GridColDef[] = [
    {
      field: "customerEmail",
      headerName: t("loyalty.columns.email"),
      flex: 1.5,
      minWidth: 200,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "loyaltyPlan",
      headerName: t("loyalty.columns.category"),
      flex: 1,
      minWidth: 150,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueGetter: (_value: any, row: any) => {
        const plan = row?.loyaltyPlan;
        if (!plan) return "-";
        return plan.charAt(0).toUpperCase() + plan.slice(1).toLowerCase();
      },
    },
    {
      field: "active",
      headerName: t("loyalty.columns.status"),
      flex: 1,
      minWidth: 130,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueGetter: (_value: any, row: any) => {
        if (row?.active === true) return t("loyalty.status.active");
        if (row?.active === false) return t("loyalty.status.inactive");
        return "-";
      },
    },
    {
      field: "updatedAt",
      headerName: t("loyalty.columns.updatedAt"),
      flex: 1.2,
      minWidth: 180,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: any) =>
        value ? dayjs(value).format("DD/MM/YYYY") : "-",
    },
    {
      field: "actions",
      headerName: t("loyalty.columns.actions"),
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerClassName: "header-style",
      renderCell: (params) => (
        <Tooltip title={t("button.Edit")}>
          <IconButton
            size="small"
            onClick={(e) => handleEditClick(e, params.row)}
          >
            <EditIcon sx={{ fontSize: 20, color: "var(--blue-400)" }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <BoxContent
      title={t("loyalty.pageTitle")}
      button
      titleButton={t("loyalty.generateReport.button")}
      handleClick={() => setOpenModalReport(true)}
      button2
      titleButton2={t("loyalty.add.button")}
      handleClick2={() => setOpenModalAdd(true)}
    >
      <QuickAlert
        visibility={visibleAlert}
        description={messageQuickAlert}
        severity={severityAlert}
        title={titleMessage}
        hanhleClose={() => setVisibleAlert(false)}
      />

      <Box sx={{ marginBottom: 2 }}>
        <span style={{ fontSize: 15, fontWeight: 400 }}>
          {t("loyalty.description")}
        </span>
      </Box>

      <Box sx={{ marginBottom: 2 }}>
        <span style={{ fontSize: 12 }}>{t("loyalty.searchLabel")}</span>
      </Box>

      <DataGridApi
        onRowClick={() => {}}
        dataUpdated={dataUpdated}
        endPoint="loyalty?"
        columnsData={columns}
        messageError={messageError}
        onEmptyMessageApi={t("loyalty.emptyState")}
        labelParams1={t("loyalty.searchPlaceholder")}
        globalSearchEnabled
        globalSearchParam="&search="
        cleanButtonText={t("loyalty.cleanButton")}
        cleanButtonType="outlined"
      />

      {openModalAdd && (
        <UserModal
          mode="add"
          onClose={() => setOpenModalAdd(false)}
          onSuccess={watchDataChange}
          onError={messageError}
        />
      )}

      {openModalEdit && selectedRow && (
        <UserModal
          mode="edit"
          loyaltyId={selectedRow.id}
          initialValues={{
            email: selectedRow.customerEmail,
            category: selectedRow.loyaltyPlan,
            active: selectedRow.active,
          }}
          onClose={() => setOpenModalEdit(false)}
          onSuccess={watchDataChange}
          onError={messageError}
        />
      )}

      {openModalReport && (
        <ModalLoyaltyReport
          onClose={() => setOpenModalReport(false)}
          openSucessAlert={openReportSuccessAlert}
          openErrorAlert={messageError}
        />
      )}
    </BoxContent>
  );
}
